import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from '../hooks/useBackend';

interface Classifier {
  classifier_id: number;
  classifier_type: string;
  classifier_params: Record<string, any>;
}

interface CreateClassifierRequest {
  classifier_type: string;
  classifier_params: Record<string, any>;
}

interface Narrative {
  narrative_id: number;
  name: string;
  description: string | null;
  narrative_type: 'related' | 'sentiment' | 'stance';
}

interface NarrativeStanceConfig {
  narrative_id: number;
  stance: string;
}

interface ClassifierModalProps {
  classifier?: Classifier;
  isOpen: boolean;
  onClose: () => void;
  onSave: (classifier: CreateClassifierRequest) => void;
  isLoading: boolean;
  availableTypes: string[];
  narratives: Narrative[];
}

const ClassifierModal: React.FC<ClassifierModalProps> = ({ 
  classifier, 
  isOpen, 
  onClose, 
  onSave, 
  isLoading, 
  availableTypes,
  narratives 
}) => {
  const [formData, setFormData] = useState<CreateClassifierRequest>(() => ({
    classifier_type: classifier?.classifier_type || '',
    classifier_params: classifier?.classifier_params || {},
  }));
  
  const [paramsJson, setParamsJson] = useState<string>(() => 
    JSON.stringify(classifier?.classifier_params || {}, null, 2)
  );
  const [jsonError, setJsonError] = useState<string | null>(null);
  
  // For narrative_mappings classifier type
  const [narrativeStances, setNarrativeStances] = useState<NarrativeStanceConfig[]>(() => {
    if (classifier?.classifier_type === 'narrative_mappings' && classifier.classifier_params.narrative_to_wanted_stance) {
      return Object.entries(classifier.classifier_params.narrative_to_wanted_stance).map(([id, stance]) => ({
        narrative_id: parseInt(id),
        stance: stance as string
      }));
    }
    return [];
  });

  // Update form when classifier changes (for edit mode)
  React.useEffect(() => {
    if (classifier) {
      setFormData({
        classifier_type: classifier.classifier_type,
        classifier_params: classifier.classifier_params,
      });
      setParamsJson(JSON.stringify(classifier.classifier_params, null, 2));
      
      // Update narrative stances if it's a narrative_mappings classifier
      if (classifier.classifier_type === 'narrative_mappings' && classifier.classifier_params.narrative_to_wanted_stance) {
        setNarrativeStances(
          Object.entries(classifier.classifier_params.narrative_to_wanted_stance).map(([id, stance]) => ({
            narrative_id: parseInt(id),
            stance: stance as string
          }))
        );
      } else {
        setNarrativeStances([]);
      }
    } else {
      // Reset for create mode
      setFormData({
        classifier_type: '',
        classifier_params: {},
      });
      setParamsJson('{}');
      setNarrativeStances([]);
    }
    setJsonError(null);
  }, [classifier]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // For narrative_mappings, build params from narrativeStances
    if (formData.classifier_type === 'narrative_mappings') {
      if (narrativeStances.length === 0) {
        setJsonError('Please add at least one narrative configuration');
        return;
      }
      
      const narrative_to_wanted_stance: Record<number, string> = {};
      narrativeStances.forEach(config => {
        narrative_to_wanted_stance[config.narrative_id] = config.stance;
      });
      
      const finalData = {
        ...formData,
        classifier_params: { narrative_to_wanted_stance }
      };
      onSave(finalData);
      return;
    }
    
    // For other types, validate JSON
    try {
      const parsedParams = JSON.parse(paramsJson);
      const finalData = { ...formData, classifier_params: parsedParams };
      onSave(finalData);
    } catch (error) {
      setJsonError('Invalid JSON format');
    }
  };

  const handleTypeChange = (type: string) => {
    setFormData(prev => ({ ...prev, classifier_type: type }));
    
    // Set default parameters based on type
    const defaultParams = getDefaultParams(type);
    setParamsJson(JSON.stringify(defaultParams, null, 2));
    setJsonError(null);
    
    // Reset narrative stances when type changes
    if (type === 'narrative_mappings') {
      setNarrativeStances([]);
    }
  };

  const handleJsonChange = (value: string) => {
    setParamsJson(value);
    setJsonError(null);
    
    // Try to parse and update formData
    try {
      const parsed = JSON.parse(value);
      setFormData(prev => ({ ...prev, classifier_params: parsed }));
    } catch (error) {
      // Invalid JSON, but don't show error until submit
    }
  };

  const getDefaultParams = (type: string): Record<string, any> => {
    switch (type) {
      case 'simple_text':
        return {
          play_video_words: ["example", "keyword"]
        };
      case 'clip':
        return {
          threshold: 0.963,
          use_labels_from_persona: true
        };
      case 'origin':
        return {
          play_video_origins: ["SG", "CN"]
        };
      case 'category':
        return {
          categories: ["Current Affairs", "Animals"]
        };
      case 'cluster':
        return {
          cluster_ids: ["10024", "10087"]
        };
      case 'narrative_mappings':
        return {
          narrative_to_wanted_stance: {}
        };
      default:
        return {};
    }
  };

  const getTypeDescription = (type: string): string => {
    switch (type) {
      case 'simple_text':
        return 'Text-based classification using keyword matching';
      case 'clip':
        return 'Visual classification using CLIP model';
      case 'origin':
        return 'Origin-based classification using video origin using ISO 3166-1 alpha-2 codes (e.g. SG, CN)';
      case 'category':
        return 'Category-based classification using video categories';
      case 'cluster':
        return 'Cluster-based classification using TikTok video cluster IDs from pack_vod_scores';
      case 'narrative_mappings':
        return 'Narrative-based classification using AI-powered narrative detection';
      default:
        return 'Custom classifier type';
    }
  };

  // Helper to get stance options based on narrative type
  const getStanceOptions = (narrativeType: 'related' | 'sentiment' | 'stance'): string[] => {
    if (narrativeType === 'related') {
      return ['related', 'unrelated'];
    }
    return ['pro', 'against', 'neutral'];
  };

  // Helper to map related/unrelated to pro/against for storage
  const mapStanceToStorage = (stance: string, narrativeType: 'related' | 'sentiment' | 'stance'): string => {
    if (narrativeType === 'related') {
      return stance === 'related' ? 'pro' : 'against';
    }
    return stance;
  };

  // Helper to map pro/against back to related/unrelated for display
  const mapStanceFromStorage = (stance: string, narrativeType: 'related' | 'sentiment' | 'stance'): string => {
    if (narrativeType === 'related') {
      return stance === 'pro' ? 'related' : 'unrelated';
    }
    return stance;
  };

  // Add a new narrative configuration
  const handleAddNarrative = () => {
    const selectedNarrativeId = parseInt((document.getElementById('narrative-select') as HTMLSelectElement)?.value);
    if (!selectedNarrativeId || isNaN(selectedNarrativeId)) return;
    
    // Check if already added
    if (narrativeStances.some(ns => ns.narrative_id === selectedNarrativeId)) {
      setJsonError('This narrative is already added');
      return;
    }
    
    const narrative = narratives.find(n => n.narrative_id === selectedNarrativeId);
    if (!narrative) return;
    
    // Default stance based on narrative type
    const defaultStance = narrative.narrative_type === 'related' ? 'related' : 'pro';
    const storageStance = mapStanceToStorage(defaultStance, narrative.narrative_type);
    
    setNarrativeStances([...narrativeStances, { 
      narrative_id: selectedNarrativeId, 
      stance: storageStance 
    }]);
    setJsonError(null);
  };

  // Update stance for a narrative
  const handleUpdateStance = (narrativeId: number, displayStance: string) => {
    const narrative = narratives.find(n => n.narrative_id === narrativeId);
    if (!narrative) return;
    
    const storageStance = mapStanceToStorage(displayStance, narrative.narrative_type);
    setNarrativeStances(narrativeStances.map(ns => 
      ns.narrative_id === narrativeId 
        ? { ...ns, stance: storageStance }
        : ns
    ));
  };

  // Remove a narrative configuration
  const handleRemoveNarrative = (narrativeId: number) => {
    setNarrativeStances(narrativeStances.filter(ns => ns.narrative_id !== narrativeId));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{classifier ? 'Edit Classifier' : 'Create New Classifier'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="classifier-form">
          <div className="form-group">
            <label htmlFor="classifier_type">Classifier Type *</label>
            <select
              id="classifier_type"
              value={formData.classifier_type}
              onChange={(e) => handleTypeChange(e.target.value)}
              required
            >
              <option value="">Select classifier type...</option>
              {availableTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {formData.classifier_type && (
              <div className="type-description">
                {getTypeDescription(formData.classifier_type)}
              </div>
            )}
          </div>

          {formData.classifier_type === 'narrative_mappings' ? (
            <div className="form-group narrative-mappings-config">
              <label>Narrative Configurations *</label>
              
              <div className="narrative-selector">
                <select id="narrative-select" className="narrative-dropdown">
                  <option value="">Select a narrative...</option>
                  {narratives
                    .filter(n => !narrativeStances.some(ns => ns.narrative_id === n.narrative_id))
                    .map(narrative => (
                      <option key={narrative.narrative_id} value={narrative.narrative_id}>
                        {narrative.name} ({narrative.narrative_type})
                      </option>
                    ))}
                </select>
                <button 
                  type="button" 
                  onClick={handleAddNarrative}
                  className="btn-secondary"
                >
                  ➕ Add Narrative
                </button>
              </div>

              {jsonError && (
                <div className="field-error">{jsonError}</div>
              )}

              <div className="narrative-configs-list">
                {narrativeStances.length === 0 ? (
                  <div className="empty-message">No narratives configured. Add at least one narrative above.</div>
                ) : (
                  narrativeStances.map(config => {
                    const narrative = narratives.find(n => n.narrative_id === config.narrative_id);
                    if (!narrative) return null;
                    
                    const displayStance = mapStanceFromStorage(config.stance, narrative.narrative_type);
                    const stanceOptions = getStanceOptions(narrative.narrative_type);
                    
                    return (
                      <div key={config.narrative_id} className="narrative-config-item">
                        <div className="narrative-info">
                          <strong>{narrative.name}</strong>
                          <span className={`narrative-type-badge ${narrative.narrative_type}`}>{narrative.narrative_type}</span>
                        </div>
                        <div className="stance-selector">
                          <label>
                            {narrative.narrative_type === 'related' 
                              ? '📍 Desired Relevance' 
                              : narrative.narrative_type === 'sentiment'
                              ? '💭 Desired Sentiment'
                              : '⚡ Desired Stance'}
                          </label>
                          <select
                            value={displayStance}
                            onChange={(e) => handleUpdateStance(config.narrative_id, e.target.value)}
                          >
                            {stanceOptions.map(option => (
                              <option key={option} value={option}>
                                {option.charAt(0).toUpperCase() + option.slice(1)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveNarrative(config.narrative_id)}
                          className="btn-icon btn-danger"
                          title="Remove narrative"
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="field-help">
                Select narratives and configure the desired stance for each. Videos matching these criteria will be played by the bot.
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="classifier_params">Parameters (JSON) *</label>
              <textarea
                id="classifier_params"
                value={paramsJson}
                onChange={(e) => handleJsonChange(e.target.value)}
                placeholder="Enter classifier parameters as JSON..."
                rows={10}
                className={`json-editor ${jsonError ? 'error' : ''}`}
                required
              />
              {jsonError && (
                <div className="field-error">{jsonError}</div>
              )}
              <div className="field-help">
                Enter parameters as valid JSON. The structure depends on the classifier type.
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (classifier ? 'Update Classifier' : 'Create Classifier')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const ClassifierManager: React.FC = () => {
  const queryClient = useQueryClient();
  const backend = useBackend();
  
  const [selectedClassifier, setSelectedClassifier] = useState<Classifier | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Fetch classifiers
  const { data: classifiers = [], isLoading, error, refetch } = useQuery<Classifier[]>({
    queryKey: ['classifiers'],
    queryFn: async () => {
      const response = await fetch('/api/classifiers');
      if (!response.ok) throw new Error('Failed to fetch classifiers');
      return response.json();
    },
    refetchInterval: 30000,
  });

  // Fetch available classifier types
  const { data: availableTypes = [] } = useQuery<string[]>({
    queryKey: ['classifier-types'],
    queryFn: async () => {
      const response = await fetch('/api/classifiers/types');
      if (!response.ok) throw new Error('Failed to fetch classifier types');
      return response.json();
    },
  });

  // Fetch narratives for narrative_mappings classifier
  const { data: narratives = [] } = useQuery<Narrative[]>({
    queryKey: ['narratives'],
    queryFn: async () => {
      const response = await fetch('/api/narratives');
      if (!response.ok) throw new Error('Failed to fetch narratives');
      return response.json();
    },
  });

  // Create classifier mutation
  const createClassifierMutation = useMutation({
    mutationFn: async (classifierData: CreateClassifierRequest) => {
      const response = await fetch('/api/classifiers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(classifierData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create classifier');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedClassifier(undefined);
      queryClient.invalidateQueries({ queryKey: ['classifiers'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  // Update classifier mutation
  const updateClassifierMutation = useMutation({
    mutationFn: async ({ classifierId, classifierData }: { classifierId: number; classifierData: CreateClassifierRequest }) => {
      const response = await fetch(`/api/classifiers/${classifierId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(classifierData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update classifier');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedClassifier(undefined);
      queryClient.invalidateQueries({ queryKey: ['classifiers'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  // Delete classifier mutation
  const deleteClassifierMutation = useMutation({
    mutationFn: async (classifierId: number) => {
      const response = await fetch(`/api/classifiers/${classifierId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete classifier');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['classifiers'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const handleCreateClassifier = useCallback(() => {
    setSelectedClassifier(undefined);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleEditClassifier = useCallback((classifier: Classifier) => {
    setSelectedClassifier(classifier);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleDeleteClassifier = useCallback((classifier: Classifier) => {
    if (window.confirm(`Are you sure you want to delete classifier "${classifier.classifier_type}"?`)) {
      deleteClassifierMutation.mutate(classifier.classifier_id);
    }
  }, [deleteClassifierMutation]);

  const handleSaveClassifier = useCallback((classifierData: CreateClassifierRequest) => {
    if (selectedClassifier) {
      updateClassifierMutation.mutate({ classifierId: selectedClassifier.classifier_id, classifierData });
    } else {
      createClassifierMutation.mutate(classifierData);
    }
  }, [selectedClassifier, createClassifierMutation, updateClassifierMutation]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedClassifier(undefined);
    setActionError(null);
  }, []);

  const getClassifierIcon = (type: string): string => {
    switch (type) {
      case 'simple_text': return '📝';
      case 'clip': return '🖼️';
      case 'origin': return '🌍';
      case 'category': return '🏷️';
      case 'cluster': return '🎯';
      case 'narrative_mappings': return '📖';
      default: return '🔧';
    }
  };

  const formatParameters = (params: Record<string, any>): string => {
    return JSON.stringify(params, null, 2);
  };

  if (isLoading) return <div className="loading">Loading classifiers...</div>;
  if (error) return <div className="error">Error loading classifiers: {(error as Error).message}</div>;

  return (
    <div className="classifier-manager">
      <div className="page-header">
        <h1>🔧 Classifier Management</h1>
        <div className="page-actions">
          <button onClick={() => refetch()} className="btn-secondary">
            🔄 Refresh
          </button>
          <button onClick={handleCreateClassifier} className="btn-primary">
            ➕ Add Classifier
          </button>
        </div>
      </div>

      {actionError && (
        <div className="error-banner">
          <span>⚠️ {actionError}</span>
          <button onClick={() => setActionError(null)}>×</button>
        </div>
      )}

      <div className="classifiers-grid">
        {classifiers.map((classifier) => (
          <div key={classifier.classifier_id} className="classifier-card">
            <div className="classifier-header">
              <div className="classifier-title">
                <span className="classifier-icon">{getClassifierIcon(classifier.classifier_type)}</span>
                <h3>{classifier.classifier_type}</h3>
                <span className="classifier-id">ID: {classifier.classifier_id}</span>
              </div>
              <div className="classifier-actions">
                <button 
                  onClick={() => handleEditClassifier(classifier)}
                  className="btn-icon"
                  title="Edit classifier"
                >
                  ✏️
                </button>
                <button 
                  onClick={() => handleDeleteClassifier(classifier)}
                  className="btn-icon btn-danger"
                  title="Delete classifier"
                >
                  🗑️
                </button>
              </div>
            </div>

            <div className="classifier-details">
              <div className="detail-section">
                <strong>Parameters:</strong>
                {classifier.classifier_type === 'narrative_mappings' && classifier.classifier_params?.narrative_to_wanted_stance ? (
                  <div className="narrative-params-display">
                    {Object.entries(classifier.classifier_params.narrative_to_wanted_stance).map(([nId, stance]) => {
                      const narrative = narratives.find(n => n.narrative_id === parseInt(nId));
                      const raw = String(stance);
                      let displayStance = raw;
                      if (narrative) {
                        if (narrative.narrative_type === 'related') {
                          displayStance = raw === 'pro' ? 'Related' : raw === 'against' ? 'Unrelated' : raw;
                        } else {
                          displayStance = raw.charAt(0).toUpperCase() + raw.slice(1);
                        }
                      }
                      return (
                        <div key={nId} className="narrative-param-row">
                          <span className="narrative-param-name">{narrative?.name || `Narrative ${nId}`}</span>
                          <span className="narrative-param-stance">{displayStance}</span>
                        </div>
                      );
                    })}
                    {Object.keys(classifier.classifier_params.narrative_to_wanted_stance).length === 0 && (
                      <div className="narrative-param-row" style={{ opacity: 0.6 }}>No narratives configured</div>
                    )}
                  </div>
                ) : (
                  <pre className="params-preview">
                    {formatParameters(classifier.classifier_params)}
                  </pre>
                )}
              </div>
            </div>
          </div>
        ))}

        {classifiers.length === 0 && (
          <div className="empty-state">
            <h3>No classifiers configured</h3>
            <p>Add your first classifier to enable content classification for bot groups.</p>
            <button onClick={handleCreateClassifier} className="btn-primary">
              ➕ Add First Classifier
            </button>
          </div>
        )}
      </div>

      <ClassifierModal
        classifier={selectedClassifier}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveClassifier}
        isLoading={createClassifierMutation.isPending || updateClassifierMutation.isPending}
        availableTypes={availableTypes}
        narratives={narratives}
      />
    </div>
  );
};

export default ClassifierManager; 